import React from "react";
import dayjs from "dayjs";
import {
    CartesianGrid,
    ComposedChart,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    Legend,
    Brush,
} from "recharts";
import { isMobileViewport } from "../../utils/responsive";
import { getPaddedDomain } from "../../utils/chartDomain";
import { formatCompactNumber, formatCompactNumberKR } from "../../utils/numberFormat";
import { useThemeColors, getThemeColors } from "../../utils/themeColors";
import ChartCrosshairCursor from "../common/chart_crosshair_cursor";
import FilterPill from "../common/filter_pill";

const formatCurrency = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) return "";
    return Number(value).toLocaleString();
};

const collectSeriesValues = (data, keys) => {
    const values = [];
    if (!Array.isArray(data) || !Array.isArray(keys)) return values;
    data.forEach((entry) => {
        if (!entry) return;
        keys.forEach((key) => {
            const value = entry[key];
            if (Number.isFinite(value)) values.push(value);
        });
    });
    return values;
};

class HistoryChart extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            seriesVisibility: {
                riskyKRW: false,
                fiatKRW: false,
                totalKRW: true,
                riskyUSD: false,
                rateUSD: false,
            },
            rangeKey: "all",
            chartData: this.buildChartData(props.balanceHistory),
        };
    }

    getDefaultSeriesVisibility = () => ({
        riskyKRW: false,
        fiatKRW: false,
        totalKRW: true,
        riskyUSD: false,
        rateUSD: false,
    });

    componentDidUpdate(prevProps) {
        if (prevProps.balanceHistory !== this.props.balanceHistory) {
            this.setState({
                chartData: this.buildChartData(this.props.balanceHistory),
                seriesVisibility: this.getDefaultSeriesVisibility(),
            });
        }
    }

    buildChartData = (balanceHistory) => {
        const sortedHistory = (balanceHistory || [])
            .filter((item) => item && item.created)
            .slice()
            .sort((a, b) => new Date(a.created) - new Date(b.created));

        return sortedHistory.map((item) => ({
            time: new Date(item.created).getTime(),
            riskyKRW: Number(item.risky_balance_krw),
            fiatKRW: Number(item.fiat_balance),
            totalKRW: Number(item.total_balance),
            riskyUSD: Number(item.risky_balance),
            rateUSD: Number(item.exchange_rate),
        }));
    };

    toggleSeries = (key) => {
        this.setState((prev) => ({
            seriesVisibility: { ...prev.seriesVisibility, [key]: !prev.seriesVisibility[key] },
        }));
    };

    setRange = (rangeKey) => { this.setState({ rangeKey }); };

    render() {
        const isMobile = isMobileViewport();
        const C = this.props.themeColors || getThemeColors();
        const { seriesVisibility, rangeKey, chartData } = this.state;
        const rangeOptions = [
            { key: "1d", label: "1일", hours: 24 },
            { key: "7d", label: "7일", hours: 24 * 7 },
            { key: "14d", label: "14일", hours: 24 * 14 },
            { key: "20d", label: "20일", hours: 24 * 20 },
            { key: "30d", label: "30일", hours: 24 * 30 },
            { key: "all", label: "전체", hours: null },
        ];
        const selectedRange = rangeOptions.find((o) => o.key === rangeKey) || rangeOptions[rangeOptions.length - 1];
        const displayData = selectedRange.hours ? chartData.slice(-selectedRange.hours) : chartData;

        const height = isMobile ? 220 : 360;
        const leftAxisWidth = isMobile ? 56 : 90;
        const rightAxisWidth = isMobile ? 52 : 70;
        const chartMargin = { top: 10, right: isMobile ? 12 : rightAxisWidth, left: isMobile ? 8 : leftAxisWidth / 2, bottom: 0 };
        const formatAxisValue = (value, prefix, formatter) => {
            const formatted = isMobile ? formatter(value) : formatCurrency(value);
            return formatted ? `${prefix}${formatted}` : "";
        };
        const showLeftAxis = seriesVisibility.riskyKRW || seriesVisibility.fiatKRW || seriesVisibility.totalKRW;
        const showRightAxis = seriesVisibility.riskyUSD || seriesVisibility.rateUSD;
        const hasSeries = showLeftAxis || showRightAxis;
        const leftAxisKeys = [];
        const rightAxisKeys = [];
        if (seriesVisibility.riskyKRW) leftAxisKeys.push("riskyKRW");
        if (seriesVisibility.fiatKRW) leftAxisKeys.push("fiatKRW");
        if (seriesVisibility.totalKRW) leftAxisKeys.push("totalKRW");
        if (seriesVisibility.riskyUSD) rightAxisKeys.push("riskyUSD");
        if (seriesVisibility.rateUSD) rightAxisKeys.push("rateUSD");
        const leftAxisDomain = getPaddedDomain(collectSeriesValues(displayData, leftAxisKeys));
        const rightAxisDomain = getPaddedDomain(collectSeriesValues(displayData, rightAxisKeys));

        const seriesOptions = [
            { key: "riskyKRW", label: "Risky (KRW)" },
            { key: "fiatKRW", label: "Fiat (KRW)" },
            { key: "totalKRW", label: "총 잔고 (KRW)" },
            { key: "riskyUSD", label: "Risky (USD)" },
            { key: "rateUSD", label: "환율 (USD)" },
        ];
        const titleSuffix = selectedRange.hours ? `최근 ${selectedRange.label}` : "전체";

        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-3 shadow-card sm:p-4">
                <div className="text-sm font-semibold text-content-secondary">
                    단일 전략 잔고 차트 ({titleSuffix})
                </div>
                <div className="mt-2 flex flex-wrap gap-2 text-xs sm:mt-3">
                    {seriesOptions.map((option) => (
                        <label
                            key={option.key}
                            className={`flex min-h-[44px] cursor-pointer items-center gap-2 rounded-full border px-3 py-2 transition-colors ${
                                seriesVisibility[option.key]
                                    ? "border-brand bg-brand-subtle text-brand-light"
                                    : "border-surface-border text-content-secondary hover:bg-surface-hover"
                            }`}
                        >
                            <input
                                type="checkbox"
                                checked={seriesVisibility[option.key]}
                                onChange={() => this.toggleSeries(option.key)}
                                className="h-3.5 w-3.5 rounded border-surface-border-light bg-surface text-brand"
                            />
                            <span className="font-medium">{option.label}</span>
                        </label>
                    ))}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs sm:mt-3">
                    <span className="text-content-muted">기간</span>
                    {/* 기간 선택 버튼 → FilterPill solid */}
                    {rangeOptions.map((option) => (
                        <FilterPill
                            key={option.key}
                            active={option.key === selectedRange.key}
                            onClick={() => this.setRange(option.key)}
                            variant="solid"
                        >
                            {option.label}
                        </FilterPill>
                    ))}
                </div>
                <div className="mt-3 sm:mt-4" style={{ height }}>
                    {hasSeries ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={displayData} margin={chartMargin}>
                                <CartesianGrid strokeDasharray="3 3" stroke={C.surfaceElevated} />
                                <XAxis dataKey="time" type="number" tickFormatter={(v) => dayjs(v).format("MM-DD HH:mm")} tick={{ fontSize: 12, fill: C.contentSecondary }} domain={["dataMin", "dataMax"]} />
                                {showLeftAxis && (
                                    <YAxis yAxisId="left" width={leftAxisWidth} tickMargin={6} tick={{ fontSize: 12, fill: C.contentSecondary }} tickFormatter={(v) => formatAxisValue(v, "₩", formatCompactNumberKR)} domain={leftAxisDomain} />
                                )}
                                {showRightAxis && (
                                    <YAxis yAxisId="right" orientation="right" width={rightAxisWidth} tickMargin={6} tick={{ fontSize: 12, fill: C.contentSecondary }} tickFormatter={(v) => formatAxisValue(v, "$", formatCompactNumber)} domain={rightAxisDomain} />
                                )}
                                <Tooltip
                                    cursor={
                                        <ChartCrosshairCursor
                                            stroke={C.contentMuted}
                                            getDomain={(k) =>
                                                k === "riskyUSD" || k === "rateUSD"
                                                    ? rightAxisDomain
                                                    : leftAxisDomain
                                            }
                                        />
                                    }
                                    formatter={(value, name) => {
                                        const prefix = name.includes("KRW") ? "₩" : "$";
                                        return [`${prefix}${formatCurrency(value)}`, name];
                                    }}
                                    labelFormatter={(v) => dayjs(v).format("YYYY-MM-DD HH:mm")}
                                    contentStyle={{ backgroundColor: C.surfaceElevated, border: `1px solid ${C.surfaceBorder}`, borderRadius: "0.75rem" }}
                                    labelStyle={{ color: C.contentSecondary }}
                                    itemStyle={{ color: C.content }}
                                />
                                <Legend />
                                {seriesVisibility.riskyKRW && <Line type="monotone" dataKey="riskyKRW" name="Risky 잔고 (KRW)" stroke={C.brandLight} strokeWidth={2} dot={false} yAxisId="left" />}
                                {seriesVisibility.fiatKRW && <Line type="monotone" dataKey="fiatKRW" name="Fiat 잔고 (KRW)" stroke={C.gain} strokeWidth={2} dot={false} yAxisId="left" />}
                                {seriesVisibility.totalKRW && <Line type="monotone" dataKey="totalKRW" name="총 잔고 (KRW)" stroke={C.warning} strokeWidth={2} dot={false} yAxisId="left" />}
                                {seriesVisibility.riskyUSD && <Line type="monotone" dataKey="riskyUSD" name="Risky 잔고 (USD)" stroke={C.brand} strokeWidth={2} dot={false} yAxisId="right" />}
                                {seriesVisibility.rateUSD && <Line type="monotone" dataKey="rateUSD" name="환율 (USD)" stroke={C.loss} strokeWidth={2} dot={false} yAxisId="right" />}
                                {!isMobile && <Brush dataKey="time" height={24} stroke={C.brand} tickFormatter={(v) => dayjs(v).format("MM-DD HH:mm")} />}
                            </ComposedChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex h-full items-center justify-center text-sm text-content-muted">
                            표시할 항목을 선택하세요.
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

const HistoryChartWithTheme = (props) => {
    const themeColors = useThemeColors();
    return <HistoryChart {...props} themeColors={themeColors} />;
};
HistoryChartWithTheme.displayName = "HistoryChartWithTheme";
export default HistoryChartWithTheme;
